import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import ControlledTable from 'components/shared/table/ControlledTable';
import { useProfile } from 'context/profile';
import usePagination from 'hooks/usePagination';
import Classification from 'models/Classification';
import { Paginated } from 'models/Paginated';
import { PERMISSIONS } from 'models/Profile';
import { AdditionalMeta } from 'services/api/clientClassifications';
import { useHealthPlansActions, useHealthPlansStore } from 'stores/healthPlansStore';
import { theme } from 'styles/theme/theme';

import RemoveConfirmModal from '../modals/acceptedPlans/RemoveConfirmModal';

type AcceptedPlansTableProps = {
  data: Paginated<Classification, AdditionalMeta> | undefined;
  isLoading: boolean;
  pagination: ReturnType<typeof usePagination>;
};

const AcceptedPlansTable = ({ data, isLoading, pagination }: AcceptedPlansTableProps) => {
  const { profile } = useProfile();

  const filters = useHealthPlansStore((state) => state.filters);
  const sort = useHealthPlansStore((state) => state.sort);
  const { setSort } = useHealthPlansActions();

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedForRemove, setSelectedForRemove] = useState<Classification | null>(null);

  const columnHelper = createColumnHelper<Classification>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row.name, {
        header: 'Contracted Health Plans',
        id: 'name',
        meta: {
          style: {
            fontWeight: 'bold',
          },
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: '',
        enableSorting: false,
        id: 'delete',
        meta: {
          style: {
            justifyContent: 'flex-end',
          },
        },
        cell: (row) => (
          <>
            {profile.has(PERMISSIONS.clientClassificationsEdit) && (
              <StyledRemoveLink
                onClick={() => {
                  setSelectedForRemove(row.row.original);
                  setShowRemoveModal(true);
                }}>
                Remove
              </StyledRemoveLink>
            )}
          </>
        ),
      }),
    ];
  }, [profile, columnHelper]);

  return (
    <>
      <ControlledTable
        data={data?.data ?? []}
        filters={filters}
        columns={columns}
        loading={isLoading}
        pageCount={data?.meta.totalPages ?? 1}
        pagination={pagination}
        sorting={{
          sortBy: sort,
          setSortBy: setSort,
        }}
      />
      {showRemoveModal && selectedForRemove && (
        <RemoveConfirmModal
          healthPlan={selectedForRemove}
          handleClose={() => setShowRemoveModal(false)}
          clientId={profile.actingClient.id}
        />
      )}
    </>
  );
};

const StyledRemoveLink = styled.a`
  color: ${theme.colors.accentRed};
  cursor: pointer;
  text-decoration: none;
`;

export default AcceptedPlansTable;
